// store/thunks/consultationThunks.js
import { createAction, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";

// ============================================================================
// SESSION LIFECYCLE THUNKS
// ============================================================================

/**
 * Start new consultation session
 * POST /api/consultation/sessions/start
 */
export const startSessionThunk = createAsyncThunk(
  "consultation/startSession",
  async (sessionData, { rejectWithValue }) => {
    try {
      const response = await api.post(
        "/consultation/sessions/start",
        sessionData
      );
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Get session details
 * GET /api/consultation/sessions/:sessionId
 */
export const getSessionThunk = createAsyncThunk(
  "consultation/getSession",
  async (sessionId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/consultation/sessions/${sessionId}`);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Pause session
 * POST /api/consultation/sessions/:sessionId/pause
 */
export const pauseSessionThunk = createAsyncThunk(
  "consultation/pauseSession",
  async ({ sessionId, reason, adminCode, adminName }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/consultation/sessions/${sessionId}/pause`,
        { reason, adminCode, adminName }
      );
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Resume session
 * POST /api/consultation/sessions/:sessionId/resume
 */
export const resumeSessionThunk = createAsyncThunk(
  "consultation/resumeSession",
  async ({ sessionId, adminCode, adminName }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/consultation/sessions/${sessionId}/resume`,
        { adminCode, adminName }
      );
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Complete session
 * POST /api/consultation/sessions/:sessionId/complete
 */
export const completeSessionThunk = createAsyncThunk(
  "consultation/completeSession",
  async ({ sessionId, outcomeData }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/consultation/sessions/${sessionId}/complete`,
        outcomeData
      );
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ============================================================================
// ADMIN & BRANCH VIEW THUNKS
// ============================================================================

/**
 * Get admin's active sessions
 * GET /api/consultation/sessions/admin/:adminCode/active
 */
export const getAdminActiveSessionsThunk = createAsyncThunk(
  "consultation/getAdminActiveSessions",
  async (adminCode, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `/consultation/sessions/admin/${adminCode}/active`
      );
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Get branch sessions
 * GET /api/consultation/sessions/branch/:branchCode
 */
export const getBranchSessionsThunk = createAsyncThunk(
  "consultation/getBranchSessions",
  async ({ branchCode, filters = {} }, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `/consultation/sessions/branch/${branchCode}`,
        { params: filters }
      );
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ============================================================================
// CART OPERATION THUNKS
// ============================================================================

/**
 * Add item to cart
 * POST /api/consultation/sessions/:sessionId/cart/items
 */
export const addItemToCartThunk = createAsyncThunk(
  "consultation/addItemToCart",
  async ({ sessionId, itemData }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/consultation/sessions/${sessionId}/cart/items`,
        itemData
      );
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Remove item from cart
 * DELETE /api/consultation/sessions/:sessionId/cart/items/:productId
 */
export const removeItemFromCartThunk = createAsyncThunk(
  "consultation/removeItemFromCart",
  async (
    { sessionId, productId, adminCode, adminName },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.delete(
        `/consultation/sessions/${sessionId}/cart/items/${productId}`,
        { data: { adminCode, adminName } }
      );
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Update item quantity
 * PATCH /api/consultation/sessions/:sessionId/cart/items/:productId/quantity
 */
export const updateItemQuantityThunk = createAsyncThunk(
  "consultation/updateItemQuantity",
  async (
    { sessionId, productId, quantity, adminCode, adminName },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.patch(
        `/consultation/sessions/${sessionId}/cart/items/${productId}/quantity`,
        { quantity, adminCode, adminName }
      );
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Show cart to customer
 * POST /api/consultation/sessions/:sessionId/cart/show
 */
export const showCartToCustomerThunk = createAsyncThunk(
  "consultation/showCartToCustomer",
  async ({ sessionId, adminCode, adminName }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/consultation/sessions/${sessionId}/cart/show`,
        { adminCode, adminName }
      );
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ============================================================================
// PRICING THUNKS
// ============================================================================

/**
 * Calculate session pricing
 * GET /api/consultation/sessions/:sessionId/pricing
 */
export const calculatePricingThunk = createAsyncThunk(
  "consultation/calculatePricing",
  async (sessionId, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `/consultation/sessions/${sessionId}/pricing`
      );
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Add price adjustment
 * POST /api/consultation/sessions/:sessionId/pricing/adjustments
 */
export const addPriceAdjustmentThunk = createAsyncThunk(
  "consultation/addPriceAdjustment",
  async ({ sessionId, adjustmentData }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/consultation/sessions/${sessionId}/pricing/adjustments`,
        adjustmentData
      );
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Approve price adjustment
 * POST /api/consultation/sessions/:sessionId/pricing/adjustments/:adjustmentId/approve
 */
export const approvePriceAdjustmentThunk = createAsyncThunk(
  "consultation/approvePriceAdjustment",
  async (
    { sessionId, adjustmentId, adminCode, adminName },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post(
        `/consultation/sessions/${sessionId}/pricing/adjustments/${adjustmentId}/approve`,
        { adminCode, adminName }
      );
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Lock prices
 * POST /api/consultation/sessions/:sessionId/pricing/lock
 */
export const lockPricesThunk = createAsyncThunk(
  "consultation/lockPrices",
  async (
    { sessionId, lockDurationMinutes, adminCode, adminName },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post(
        `/consultation/sessions/${sessionId}/pricing/lock`,
        { lockDurationMinutes, adminCode, adminName }
      );
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Unlock prices
 * POST /api/consultation/sessions/:sessionId/pricing/unlock
 */
export const unlockPricesThunk = createAsyncThunk(
  "consultation/unlockPrices",
  async ({ sessionId, adminCode, adminName }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/consultation/sessions/${sessionId}/pricing/unlock`,
        { adminCode, adminName }
      );
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ============================================================================
// EXCHANGE ITEMS THUNKS
// ============================================================================

/**
 * Add exchange item
 * POST /api/consultation/sessions/:sessionId/exchange/items
 */
export const addExchangeItemThunk = createAsyncThunk(
  "consultation/addExchangeItem",
  async ({ sessionId, exchangeData }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/consultation/sessions/${sessionId}/exchange/items`,
        exchangeData
      );
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Adjust exchange value
 * PATCH /api/consultation/sessions/:sessionId/exchange/items/:exchangeItemId/value
 */
export const adjustExchangeValueThunk = createAsyncThunk(
  "consultation/adjustExchangeValue",
  async (
    { sessionId, exchangeItemId, adjustmentData },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.patch(
        `/consultation/sessions/${sessionId}/exchange/items/${exchangeItemId}/value`,
        adjustmentData
      );
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Accept exchange item
 * POST /api/consultation/sessions/:sessionId/exchange/items/:exchangeItemId/accept
 */
export const acceptExchangeItemThunk = createAsyncThunk(
  "consultation/acceptExchangeItem",
  async (
    { sessionId, exchangeItemId, adminCode, adminName },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post(
        `/consultation/sessions/${sessionId}/exchange/items/${exchangeItemId}/accept`,
        { adminCode, adminName }
      );
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ============================================================================
// EVENTS & TIMELINE THUNKS
// ============================================================================

/**
 * Get session events
 * GET /api/consultation/sessions/:sessionId/events
 */
export const getSessionEventsThunk = createAsyncThunk(
  "consultation/getSessionEvents",
  async ({ sessionId, filters = {} }, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `/consultation/sessions/${sessionId}/events`,
        { params: filters }
      );
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Get session timeline
 * GET /api/consultation/sessions/:sessionId/timeline
 */
export const getSessionTimelineThunk = createAsyncThunk(
  "consultation/getSessionTimeline",
  async (sessionId, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `/consultation/sessions/${sessionId}/timeline`
      );
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ============================================================================
// ORDER CREATION FROM SESSION
// ============================================================================

/**
 * Create order from session
 * POST /api/consultation/sessions/:sessionId/orders/create
 */
export const createOrderFromSessionThunk = createAsyncThunk(
  "consultation/createOrderFromSession",
  async ({ sessionId, orderData }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/consultation/sessions/${sessionId}/orders/create`,
        orderData
      );
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Update session notes
 * PATCH /api/consultation/sessions/:sessionId/notes
 */
export const updateSessionNotesThunk = createAsyncThunk(
  "consultation/updateSessionNotes",
  async (
    { sessionId, adminNotes, adminCode, adminName },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.patch(
        `/consultation/sessions/${sessionId}/notes`,
        { adminNotes, adminCode, adminName }
      );
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// Add this thunk to store/thunks/consultationThunks.js

/**
 * Remove price adjustment
 * DELETE /api/consultation/sessions/:sessionId/pricing/adjustments/:adjustmentId
 */
export const removeAdjustmentThunk = createAsyncThunk(
  "consultation/removeAdjustment",
  async (
    { sessionId, adjustmentId, adminCode, adminName },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.delete(
        `/consultation/sessions/${sessionId}/pricing/adjustments/${adjustmentId}`,
        { data: { adminCode, adminName } }
      );
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// In your consultationThunks.js or wherever you have these thunks

/**
 * Reject price adjustment
 * POST /api/consultation/sessions/:sessionId/pricing/adjustments/:adjustmentId/reject
 */
export const rejectPriceAdjustmentThunk = createAsyncThunk(
  "consultation/rejectPriceAdjustment",
  async (
    { sessionId, adjustmentId, rejectionReason, adminCode, adminName },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post(
        `/consultation/sessions/${sessionId}/pricing/adjustments/${adjustmentId}/reject`,
        {
          adminCode,
          adminName,
          rejectionReason,
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to reject adjustment"
      );
    }
  }
);

/**
 * Get pending price adjustments for approval
 * GET /api/consultation/sessions/:sessionId/pricing/adjustments/pending
 */
export const getPendingAdjustmentsThunk = createAsyncThunk(
  "consultation/getPendingAdjustments",
  async (sessionId, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `/consultation/sessions/${sessionId}/pricing/adjustments/pending`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to fetch pending adjustments"
      );
    }
  }
);

/**
 * Edit exchange item
 * PATCH /api/consultation/sessions/:sessionId/exchange/items/:exchangeItemId
 */
export const editExchangeItemThunk = createAsyncThunk(
  "consultation/editExchangeItem",
  async (
    { sessionId, exchangeItemId, updateData, adminCode, adminName },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.patch(
        `/consultation/sessions/${sessionId}/exchange/items/${exchangeItemId}`,
        {
          ...updateData,
          adminCode,
          adminName,
        }
      );
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Remove exchange item
 * DELETE /api/consultation/sessions/:sessionId/exchange/items/:exchangeItemId
 */
export const removeExchangeItemThunk = createAsyncThunk(
  "consultation/removeExchangeItem",
  async (
    { sessionId, exchangeItemId, adminCode, adminName },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.delete(
        `/consultation/sessions/${sessionId}/exchange/items/${exchangeItemId}`,
        {
          params: { adminCode, adminName }, // For DELETE, use params
        }
      );
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Unaccept exchange item (customer changed mind)
 * POST /api/consultation/sessions/:sessionId/exchange/items/:exchangeItemId/unaccept
 */
export const unacceptExchangeItemThunk = createAsyncThunk(
  "consultation/unacceptExchangeItem",
  async (
    { sessionId, exchangeItemId, reason, adminCode, adminName },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post(
        `/consultation/sessions/${sessionId}/exchange/items/${exchangeItemId}/unaccept`,
        {
          reason: reason || "Customer changed mind",
          adminCode,
          adminName,
        }
      );
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ============================================================================
// ACTIONS
// ============================================================================

export const clearSessionError = createAction("consultation/clearError");
export const clearCurrentSession = createAction(
  "consultation/clearCurrentSession"
);
export const clearSessionCart = createAction("consultation/clearSessionCart");
export const clearSessionPricing = createAction(
  "consultation/clearSessionPricing"
);
